"use client";

import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { IChangelogEntry } from "@/sanity/pages/PageChangelogTemplate";
import ChangelogEntryImage from "./ChangelogEntryImage";
import ScrambleText from "./ScrambleText";
import { ChangelogRelease, formatReleaseDate } from "./utils";

const EASE = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_LABEL = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
  transition: { duration: 0.6, ease: [...EASE] },
} as const;
const REVEAL_PIPE = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
  transition: { duration: 0.8, ease: [...EASE], delay: 0.1 },
} as const;
const REVEAL_TEXT = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
  transition: { duration: 0.8, ease: [...EASE], delay: 0.2 },
} as const;
const DRAW_DIVIDER = {
  initial: { scaleX: 0 },
  whileInView: { scaleX: 1 },
  viewport: VIEWPORT,
  transition: { duration: 1.4, ease: [...EASE] },
} as const;
const PIPES = ["text-white/10", "text-white/30", "text-white/50"];

function CategoryLabel({ label }: { label: string }) {
  return (
    <motion.p
      {...REVEAL_LABEL}
      className="m-0 font-spacemono text-[1.75rem] leading-[1.4] text-white/70 uppercase">
      {PIPES.map((color, i) => (
        <motion.span
          key={color}
          {...REVEAL_PIPE}
          transition={{ duration: 0.3, ease: "linear", delay: 0.15 + i * 0.12 }}
          aria-hidden="true"
          className={cn("tracking-[-0.14rem]", color)}>
          |
        </motion.span>
      ))}
      <span> </span>
      <ScrambleText
        text={label}
        delay={450}
        duration={650}
      />
    </motion.p>
  );
}

function ChangelogEntry({ entry, hidden, animateIn }: { entry: IChangelogEntry; hidden: boolean; animateIn: boolean }) {
  return (
    <div
      hidden={hidden}
      className={cn(animateIn && "motion-safe:animate-changelog-entry-in")}>
      <article className="flex flex-col gap-[2.5rem] lg:gap-[3rem]">
        <header className="flex max-w-[81rem] flex-col gap-[2rem]">
          <CategoryLabel label={entry.category} />
          <motion.h2
            {...REVEAL_TITLE}
            className="m-0 font-helveticaNeue text-[2.75rem] leading-[1.1] font-normal tracking-[-0.03em] text-white lg:text-[4rem] lg:tracking-normal">
            {entry.title}
          </motion.h2>
          {entry.description && (
            <motion.p
              {...REVEAL_TEXT}
              className="m-0 font-helveticaNeue text-[2rem] leading-[1.5] text-white/70">
              {entry.description}
            </motion.p>
          )}
        </header>

        <ChangelogEntryImage
          image={entry.image}
          imageMobile={entry.imageMobile}
          alt={entry.title}
          scene={entry.scene}
        />
      </article>
    </div>
  );
}

export default function ChangelogList({
  releases,
  isVisible,
  animateEntries,
}: {
  releases: ChangelogRelease[];
  isVisible: (entry: IChangelogEntry) => boolean;
  animateEntries: boolean;
}) {
  const visibleDates = releases.filter((release) => release.entries.some(isVisible)).map((release) => release.date);
  const lastVisibleDate = visibleDates[visibleDates.length - 1];

  return (
    <div className="relative mx-auto w-[157.375rem] max-w-full px-[3rem] lg:px-0 lg:pt-[12.5rem]">
      {releases.map((release) => {
        const releaseVisible = visibleDates.includes(release.date);
        const label = formatReleaseDate(release.date);

        return (
          <React.Fragment key={release.date}>
            <section
              hidden={!releaseVisible}
              aria-label={label}
              className="flex flex-col gap-[6rem] pt-[6rem] pb-[7rem] lg:flex-row lg:gap-[21.75rem] lg:p-0 [&[hidden]]:!hidden">
              <div className="shrink-0 lg:w-[12.875rem]">
                <motion.time
                  {...REVEAL_LABEL}
                  dateTime={release.date}
                  className="block font-spacemono text-[1.75rem] leading-[1.4] whitespace-nowrap text-white/70 uppercase lg:sticky lg:top-[15rem]">
                  <ScrambleText
                    text={label}
                    delay={150}
                    duration={700}
                  />
                </motion.time>
              </div>

              <div className="flex w-full min-w-0 flex-col gap-[6rem] lg:w-[88rem] lg:gap-[7rem]">
                {release.entries.map((entry) => (
                  <ChangelogEntry
                    key={entry._key}
                    entry={entry}
                    hidden={!isVisible(entry)}
                    animateIn={animateEntries}
                  />
                ))}
              </div>
            </section>

            {releaseVisible && release.date !== lastVisibleDate && (
              <motion.hr
                {...DRAW_DIVIDER}
                className="-mx-[3rem] h-px origin-left border-0 bg-white/10 lg:mx-0 lg:my-[11.125rem] lg:bg-white/[0.16]"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
